import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import styled from 'styled-components';

import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import BookingToast from './components/BookingToast';
import CookieConsent from './components/CookieConsent';
import LiveChat from './components/LiveChat';
import ErrorBoundary from './components/ErrorBoundary';
import BackToTop from './components/BackToTop';
import DevForm from './dev-form';
import PriceGuide from './price-guide';
import { useAnalytics } from './hooks/useAnalytics';

// Lazy-loaded components
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const NotFound = lazy(() => import('./components/NotFound'));

// Styled containers
const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  color: #ffffff;
  overflow-x: hidden;
  transition: all 0.3s ease;

  &[data-theme="light"] {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
    color: #0f172a;
  }
`;

const MainContent = styled.main`
  position: relative;
`;

const AppLoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  font-size: 1.2rem;
  color: #8b5cf6;
`;

function App() {
  const [showCookies, setShowCookies] = useState(false);
  useAnalytics(); // Initialize analytics

  useEffect(() => {
    const consent = localStorage.getItem('cookiesConsent');
    if (!consent) setShowCookies(true);
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookiesConsent', 'true');
    setShowCookies(false);
  };

  const handleDeclineCookies = () => {
    localStorage.setItem('cookiesConsent', 'declined');
    setShowCookies(false);
  };

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AppContainer>
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  color: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                },
              }}
            />
            <Header />
            <MainContent>
              <Routes>
                <Route path="/" element={
                  <>
                    <Hero />
                    <About />
                    <Projects />
                    <Testimonials />
                    <Contact />
                    <BookingToast />
                  </>
                } />
                <Route path="/dev-form" element={<DevForm />} />
                <Route path="/price-guide" element={<PriceGuide />} />
                <Route path="/privacy-policy" element={
                  <Suspense fallback={<AppLoadingSpinner>Loading Privacy Policy...</AppLoadingSpinner>}>
                    <PrivacyPolicy />
                  </Suspense>
                } />
                <Route path="/terms-of-service" element={
                  <Suspense fallback={<AppLoadingSpinner>Loading Terms of Service...</AppLoadingSpinner>}>
                    <TermsOfService />
                  </Suspense>
                } />
                <Route path="*" element={
                  <Suspense fallback={<AppLoadingSpinner>Loading...</AppLoadingSpinner>}>
                    <NotFound />
                  </Suspense>
                } />
              </Routes>
            </MainContent>
          <Footer />
                     <BackToTop />
                     <LiveChat />
            <CookieConsent 
              isVisible={showCookies}
              onAccept={handleAcceptCookies}
              onDecline={handleDeclineCookies}
            />
          </AppContainer>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
